import React, { useMemo, useState } from 'react'
import classNames from 'classnames'
import { skipToken } from '@reduxjs/toolkit/query'
import { type BookingClient, EmailTemplatePurpose, type LocationRef, type SchedulingUser } from '../../../models/Scheduling'
import { type User } from '../../../models/User'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { useGetAccountQuery } from '../../../store/accountUserSettings/accountUserSettings'
import {
  useGetAccountSchedulingSettingsQuery,
  useGetSchedulingEventTypesQuery,
  useGetSchedulingLocationsQuery,
  useGetSchedulingUsersQuery,
  useGetUserSchedulingLocationsQuery,
  useGetUserSchedulingSettingsQuery,
} from '../../../store/scheduling/scheduling'
import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { Button } from '../../../components/Button/Button'
import { Spinner } from '../../../components/Spinner/Spinner'
import { useSchedulingEmailTemplates } from '../../Scheduling/shared/useSchedulingEmailTemplates'
import { clientDisplayName } from '../../Scheduling/shared/schedulingDisplay'
import { resolveLocationPreviewId } from '../../Scheduling/flows/form/scheduleHelpers'
import { buildEmailTemplateContext } from '../../Scheduling/flows/buildEmailTemplateContext'
import { renderEmailHtmlBody, renderEmailSubject } from '../../Scheduling/flows/renderEmailTemplate'
import { sanitizeEmailHtml } from '../../Scheduling/flows/emailHtml'
import { unsubscribeLink } from '../../Scheduling/flows/clientInviteLinks'
import { EmailField } from '../../Scheduling/flows/InvitationEmailPreview'
import previewStyles from '../../Scheduling/flows/InvitationEmailPreview.module.scss'
import { type HostSelection } from './eventTypeCoverage'
import { clientEligibility, type EligibilityInput, hostBookingLink, type SkipReason, toBookingClient } from './invitationRecipients'
import { type SendBookingInvitationsOwners } from './useSendBookingInvitationsOwners'
import {
  PREVIEW_LOAD_ERROR,
  PREVIEW_NEXT,
  PREVIEW_NO_TEMPLATE,
  PREVIEW_PICK_EVENT_TYPE,
  PREVIEW_PREVIOUS,
  PREVIEW_TEMPLATES_ERROR,
  PREVIEW_TITLE,
  UNKNOWN_USER,
  previewNoEmail,
  previewNoOwner,
  previewOwnerNotShared,
  previewOwnerOff,
  previewPosition,
  previewUnsubscribed,
} from './sendBookingInvitationsCopy'

const PURPOSE = EmailTemplatePurpose.INVITATION_TO_BOOK

const EMPTY_SCHEDULING_USERS: SchedulingUser[] = []

interface PreviewedClient {
  client: BookingClient
  senderId: string
  skipReason: SkipReason | null
}

const previewedClients = (clientIds: string[], clients: User[], input: EligibilityInput, currentUserId: string): PreviewedClient[] => {
  const clientsById = new Map(clients.map((client) => [client.id, client]))
  const previewed = clientIds.flatMap((clientId): PreviewedClient[] => {
    const client = clientsById.get(clientId)
    if (!client) {
      return []
    }
    const { hostId, skipReason } = clientEligibility(client, input)
    return [{ client: toBookingClient(client), senderId: hostId ?? input.ownerIdByClientId[clientId] ?? currentUserId, skipReason }]
  })
  return [...previewed.filter((entry) => entry.skipReason === null), ...previewed.filter((entry) => entry.skipReason !== null)]
}

const skipBanner = ({ client, skipReason }: PreviewedClient, eventTypeName: string): string | null => {
  const firstName = client.firstName || clientDisplayName(client)
  switch (skipReason) {
    case 'noEmail':
      return previewNoEmail(firstName)
    case 'unsubscribed':
      return previewUnsubscribed(firstName)
    case 'noOwner':
      return previewNoOwner(firstName)
    case 'ownerNotShared':
      return previewOwnerNotShared(firstName)
    case 'ownerOff':
      return previewOwnerOff(firstName, eventTypeName)
    default:
      return null
  }
}

interface InvitationPreviewProps {
  clientIds: string[]
  owners: SendBookingInvitationsOwners
  hostSelection: HostSelection
  eventTypeId: string | null
  location: LocationRef | null
}

export const InvitationPreview: React.FC<InvitationPreviewProps> = ({ clientIds, owners, hostSelection, eventTypeId, location }) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const accountId = useAppSelector(selectAccountId)
  const currentUser = useAppSelector(selectSafeAuthenticatedUser)
  const { data: accountResponse } = useGetAccountQuery({ accountId })
  const { data: accountSettings } = useGetAccountSchedulingSettingsQuery({ accountId })
  const { data: locations } = useGetSchedulingLocationsQuery({ accountId })
  const { data: eventTypes } = useGetSchedulingEventTypesQuery({ accountId })
  const { currentData: enabledUsers, isError: enabledUsersError } = useGetSchedulingUsersQuery(eventTypeId ? { accountId, eventTypeId } : skipToken)
  const { byPurpose, isLoading: templatesLoading, isError: templatesError } = useSchedulingEmailTemplates()

  const eventType = eventTypes?.find((candidate) => candidate.id === eventTypeId)
  const hostsById = useMemo(() => new Map((enabledUsers ?? EMPTY_SCHEDULING_USERS).map((user) => [user.id, user])), [enabledUsers])
  const previewed = useMemo(
    () =>
      previewedClients(
        clientIds,
        owners.clients,
        {
          ownerIdByClientId: owners.ownerIdByClientId,
          unsubscribedClientIds: owners.unsubscribedClientIds,
          hostSelection,
          enabledUserIds: new Set(hostsById.keys()),
        },
        currentUser.id,
      ),
    [clientIds, owners.clients, owners.ownerIdByClientId, owners.unsubscribedClientIds, hostSelection, hostsById, currentUser.id],
  )
  const teammatesById = useMemo(() => new Map<string, User>(owners.teammates.map((teammate) => [teammate.id, teammate])), [owners.teammates])

  const pageIndex = Math.min(activeIndex, Math.max(previewed.length - 1, 0))
  const recipient = previewed[pageIndex]
  const host = recipient ? teammatesById.get(recipient.senderId) : undefined
  const senderQuery = location && recipient ? { accountId, userId: recipient.senderId } : skipToken
  const { currentData: senderSettings, isFetching: senderSettingsFetching } = useGetUserSchedulingSettingsQuery(senderQuery)
  const { currentData: senderLocations, isFetching: senderLocationsFetching } = useGetUserSchedulingLocationsQuery(senderQuery)
  const template = byPurpose(PURPOSE)
  const hasTemplate = Boolean(template?.subject && template?.body)

  const message = (() => {
    if (!eventType) {
      return PREVIEW_PICK_EVENT_TYPE
    }
    if (templatesError) {
      return PREVIEW_TEMPLATES_ERROR
    }
    if (owners.isError || enabledUsersError) {
      return PREVIEW_LOAD_ERROR
    }
    if (!templatesLoading && !hasTemplate) {
      return PREVIEW_NO_TEMPLATE
    }
    return null
  })()
  const isLoading = templatesLoading || owners.isLoading || !enabledUsers || senderSettingsFetching || senderLocationsFetching

  const email = (() => {
    if (message || isLoading || !eventType || !template || !recipient) {
      return null
    }
    const locationId = resolveLocationPreviewId(location, accountSettings?.defaults.locationId, senderSettings?.defaults.locationId)
    const context = buildEmailTemplateContext({
      eventType,
      client: recipient.client,
      owner: host,
      location: locationId ? ([...(locations ?? []), ...(senderLocations ?? [])].find((candidate) => candidate.id === locationId) ?? null) : null,
      bookingLink: hostBookingLink(hostsById.get(recipient.senderId), eventType) ?? '[booking link]',
      unsubscribeLink: unsubscribeLink(recipient.client, PURPOSE) ?? '[unsubscribe link]',
      account: accountResponse?.data,
    })
    return {
      subject: renderEmailSubject(template.subject, context),
      html: sanitizeEmailHtml(renderEmailHtmlBody(template.body, context), { neutralizeLinks: true }),
    }
  })()

  const banner = email && recipient && eventType ? skipBanner(recipient, eventType.name) : null

  const pager =
    previewed.length > 1 && !message && recipient ? (
      <div className='d-flex align-items-center zocks-gap-0.5 mw-0'>
        <Button
          variant='borderless'
          type='button'
          className='rounded-circle p-1 bg-transparent'
          aria-label={PREVIEW_PREVIOUS}
          disabled={pageIndex === 0}
          onClick={() => setActiveIndex(pageIndex - 1)}
        >
          <i className='bi bi-chevron-left' aria-hidden='true' />
        </Button>
        <span className='paragraph-small medium zocks-text-gray-900 text-truncate'>
          {previewPosition(clientDisplayName(recipient.client), pageIndex, previewed.length)}
        </span>
        <Button
          variant='borderless'
          type='button'
          className='rounded-circle p-1 bg-transparent'
          aria-label={PREVIEW_NEXT}
          disabled={pageIndex === previewed.length - 1}
          onClick={() => setActiveIndex(pageIndex + 1)}
        >
          <i className='bi bi-chevron-right' aria-hidden='true' />
        </Button>
      </div>
    ) : null

  const body = (() => {
    if (message) {
      return <div className='paragraph-medium zocks-text-gray-600 p-3'>{message}</div>
    }
    if (!email || !recipient) {
      return (
        <div className='d-flex justify-content-center py-5'>
          <Spinner />
        </div>
      )
    }
    return <div className={classNames(previewStyles.body, 'paragraph-medium p-3')} dangerouslySetInnerHTML={{ __html: email.html }} />
  })()

  return (
    <div className='d-flex flex-column border zocks-border-color-gray-200 zocks-border-1.0 overflow-hidden zocks-bg-color-white shadow'>
      <div className='d-flex flex-column gap-3 p-3 zocks-bg-color-gray-100'>
        <div className='d-flex align-items-center justify-content-between gap-3'>
          <h5 className='mb-0'>{PREVIEW_TITLE}</h5>
          {pager}
        </div>
        {banner ? (
          <div className='d-flex align-items-start gap-2 paragraph-small bold zocks-text-gray-700'>
            <i className='bi bi-exclamation-circle zocks-text-gray-900' aria-hidden='true' />
            <span>{banner}</span>
          </div>
        ) : null}
        {!message && email && recipient ? (
          <div className={previewStyles.fields}>
            <EmailField label='From'>
              <span className='medium'>{host ? getUserDisplayName(host) : UNKNOWN_USER} via Zocks</span>{' '}
              <span className='zocks-text-gray-500'>· bookings@zocks.io</span>
            </EmailField>
            <EmailField label='To'>
              {clientDisplayName(recipient.client)} {recipient.client.email ? <span className='zocks-text-gray-500'>· {recipient.client.email}</span> : null}
            </EmailField>
            <EmailField label='Subject'>{email.subject}</EmailField>
          </div>
        ) : null}
      </div>
      {body}
    </div>
  )
}
